import React from 'react';
import { Channel } from 'types/cx-editor';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Box,
  IconButton,
  TextField,
  Tooltip,
  Typography,
  alpha,
  CircularProgress
} from '@mui/material';
import InputAdornment from '@mui/material/InputAdornment';
import {
  ChevronDown,
  ChevronUp,
  Info,
  Mail,
  Globe,
  Newspaper,
  MessageSquare,
  Search,
  LayoutGrid
} from 'lucide-react';
import { useSelector } from 'react-redux';
import { selectLevel1Data, selectLevel2Data, selectFeatureMetricsLoading, selectFeatureMetricsError } from 'store/dataReducers/featureMetricsSlice';
import { useGlobalConfig } from 'context/GlobalContext';
import { selectSelectedPilotId } from 'store/dataReducers/pilotsSlice';
import { useAvailableFuelTypes } from 'hooks/useProjectFuelTypes';
import { useBulkManageData } from 'utils/template-list';
import {
  transformToHierarchicalData,
  getFuelTypeLabel,
  getSectionDisplayName,
  groupWebTemplatesByPage,
  type ChannelWithTemplates,
  type TemplateWithSections,
  type SectionItem
} from 'utils/templates-from-feature-metrics';

type Props = {
  selectedTemplateKey?: string;
  selectedSectionKey?: string;
  onSelectTemplate: (templateKey: string) => void;
  onSelectSection?: (templateKey: string, sectionKey: string, sectionId: string, sectionName: string) => void;
  isOpen?: boolean;
  showAllFuelTypes?: boolean;
  pilotId?: string;
};

const channelIcons: Record<string, React.ElementType> = {
  Email: Mail,
  HER: Newspaper,
  Web: Globe,
  SMS: MessageSquare,
};

const channelIconFor = (name: string) => {
  const Icon = channelIcons[name];
  return Icon ? <Icon size={16} /> : null;
};

export const DataScenariosSidebar: React.FC<Props> = ({
  selectedTemplateKey,
  selectedSectionKey,
  onSelectTemplate,
  onSelectSection,
  isOpen = true,
  showAllFuelTypes = false,
  pilotId
}) => {
  const { rc } = useGlobalConfig();
  const selectedPilotId = useSelector(selectSelectedPilotId);
  const effectivePilotId = pilotId || selectedPilotId;
  const level1Data = useSelector(selectLevel1Data);
  const level2Data = useSelector(selectLevel2Data);
  const loading = useSelector(selectFeatureMetricsLoading);
  const error = useSelector(selectFeatureMetricsError);
  const [searchQuery, setSearchQuery] = React.useState<string>('');
  const [expandedTemplates, setExpandedTemplates] = React.useState<Set<string>>(new Set());

  const { data: bulkManageData, loading: bulkManageLoading, error: bulkManageError } = useBulkManageData(rc, effectivePilotId);
  const isLoading = loading || level1Data === null || bulkManageLoading;

  const { availableFuelTypes: projectFuelTypes } = useAvailableFuelTypes(rc, effectivePilotId, true);
  const fuelType = React.useMemo(() => {
    return projectFuelTypes[0] || 'ELECTRIC';
  }, [projectFuelTypes]);

  // Transform data to hierarchical structure
  const data = React.useMemo(() => {
    // If API data exists, use it
    if (level1Data && Array.isArray(level1Data) && level1Data.length > 0) {
      const isCdfMetadata = (item: any) =>
        item?.metadata?.fuelType === 'CDF';

      const filteredLevel1 = level1Data.filter(item => !isCdfMetadata(item));
      const filteredLevel2 = Array.isArray(level2Data)
        ? level2Data.filter(item => !isCdfMetadata(item))
        : level2Data;

      return transformToHierarchicalData(filteredLevel1, filteredLevel2, projectFuelTypes, showAllFuelTypes);
    }
    return [];
  }, [level1Data, level2Data, projectFuelTypes, showAllFuelTypes]);

  // Filter based on search query
  const filteredData = React.useMemo(() => {
    if (!searchQuery.trim()) {
      return data;
    }

    const query = searchQuery.toLowerCase();
    return data
      .map(channel => ({
        ...channel,
        templates: channel.templates
          .map(template => ({
            ...template,
            sections: template.sections.filter(section =>
              section.name.toLowerCase().includes(query) ||
              section.sectionKey.toLowerCase().includes(query)
            )
          }))
          .filter(template =>
            template.name.toLowerCase().includes(query) ||
            template.templateKey.toLowerCase().includes(query) ||
            template.sections.length > 0
          )
      }))
      .filter(channel => channel.templates.length > 0);
  }, [data, searchQuery]);

  const handleToggleTemplate = (templateKey: string) => {
    setExpandedTemplates(prev => {
      const newSet = new Set(prev);
      if (newSet.has(templateKey)) {
        newSet.delete(templateKey);
      } else {
        newSet.add(templateKey);
      }
      return newSet;
    });
  };

  const handleSelectTemplate = (templateKey: string) => {
    onSelectTemplate(templateKey);
    // Auto-expand when selecting
    setExpandedTemplates(prev => new Set(prev).add(templateKey));
  };

  const handleSelectSection = (templateKey: string, sectionKey: string, sectionId: string, sectionName: string) => {
    onSelectSection?.(templateKey, sectionKey, sectionId, sectionName);
  };

  return (
    <Card sx={{ display: 'flex', flexDirection: 'column', height: '100%', borderRadius: 2, width: '100%', minWidth: isOpen ? 300 : 48, overflow: 'hidden' }}>
      <CardHeader
        sx={{
          borderBottom: theme => `1px solid ${theme.palette.divider}`,
          py: 1.5,
          px: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          minHeight: 56
        }}
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {isOpen && (
              <>
                <Typography variant="h6" component="div" sx={{ fontSize: '1rem', fontWeight: 600 }}>
                  Templates
                </Typography>
                <Tooltip title="Select a template to view and manage its data scenarios.">
                  <IconButton 
                    size="small" 
                    sx={{ 
                      p: 0.5,
                      color: theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.7)' : 'inherit'
                    }}
                  >
                    <Info size={16} style={{ opacity: 0.7 }} />
                  </IconButton>
                </Tooltip>
              </>
            )}
          </div>
        }
      />
      <CardContent sx={{ pt: 1.5, px: 2, pb: 2, flex: 1, display: isOpen ? 'flex' : 'none', flexDirection: 'column', overflow: 'hidden', minHeight: 0 }}>
        <div style={{ overflowY: 'auto', flex: 1, minHeight: 0 }}>
          {isLoading && (
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column',
              justifyContent: 'center', 
              alignItems: 'center', 
              py: 6,
              gap: 2
            }}>
              <CircularProgress size={24} />
              <Typography variant="body2" color="text.secondary">
                Loading templates...
              </Typography>
            </Box>
          )}
          {(error || bulkManageError) && !isLoading && (
            <Typography variant="body2" color="error" sx={{ py: 2, px: 1 }}>
              {error || bulkManageError}
            </Typography>
          )}

          {!isLoading && !error && (
            <>
              <TextField
                placeholder="Search templates"
                size="small"
                fullWidth
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search size={16} style={{ opacity: 0.7 }} />
                    </InputAdornment>
                  )
                }}
                sx={{ 
                  mb: 1.5,
                  '& .MuiInputAdornment-root svg': {
                    color: theme => theme.palette.mode === 'dark' ? '#fff' : theme.palette.text.secondary
                  }
                }}
              />

              {filteredData.length === 0 && (
                <Box sx={{ py: 4, px: 2, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    No templates available. Please select a project from Application Scope.
                  </Typography>
                </Box>
              )}

              {filteredData.map(channel => (
                <Accordion
                  key={channel.name}
                  disableGutters
                  elevation={0}
                  defaultExpanded={channel.name === 'Email'}
                  sx={{
                    '&:before': { display: 'none' },
                    '&.Mui-expanded': { margin: 0 }
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ChevronDown size={16} style={{ color: 'inherit' }} />}
                    sx={{
                      minHeight: 40,
                      '&.Mui-expanded': { minHeight: 40 },
                      px: 1,
                      '& .MuiAccordionSummary-expandIconWrapper': {
                        color: theme => theme.palette.mode === 'dark' ? '#fff' : theme.palette.text.secondary
                      }
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        color: 'text.secondary',
                        fontWeight: 600,
                        fontSize: '0.875rem'
                      }}
                    >
                      {channelIconFor(channel.name)}
                      {channel.name}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ pt: 0.5, pb: 0.5, px: 1 }}>
                    {channel.name === 'Web' ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        {groupWebTemplatesByPage(channel.templates || []).map(pageGroup => {
                          const pageFuelType = pageGroup.widgets.length > 0 ? pageGroup.widgets[0].fuelType : undefined;
                          const pageFuelLabel = getFuelTypeLabel(pageFuelType);
                          return (
                            <Accordion
                              key={pageGroup.pageName}
                              disableGutters
                              elevation={0}
                              defaultExpanded={false}
                              sx={{
                                '&:before': { display: 'none' },
                                '&.Mui-expanded': { margin: 0 },
                                bgcolor: 'transparent'
                              }}
                            >
                              <AccordionSummary
                                expandIcon={<ChevronDown size={14} />}
                                sx={{
                                  minHeight: 32,
                                  '&.Mui-expanded': { minHeight: 32 },
                                  px: 1,
                                  py: 0.5
                                }}
                              >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                                  <Chip
                                    size="small"
                                    variant="outlined"
                                    label={pageFuelLabel}
                                    sx={{
                                      fontSize: '0.75rem',
                                      height: 20,
                                      width: 70,
                                      px: 0.5,
                                      py: 0.5,
                                      borderColor: theme => alpha(theme.palette.primary.main, 0.5),
                                      color: 'primary.main',
                                      bgcolor: theme => alpha(theme.palette.primary.main, 0.1),
                                      flexShrink: 0,
                                      '& .MuiChip-label': { textAlign: 'center', width: '100%', px: 0 }
                                    }}
                                  />
                                  <Typography
                                    variant="body2"
                                    sx={{ fontSize: '0.8125rem', fontWeight: 500, color: 'text.secondary', flex: 1 }}
                                  >
                                    {pageGroup.pageDisplayName}
                                  </Typography>
                                </Box>
                              </AccordionSummary>
                              <AccordionDetails sx={{ pt: 0, pb: 0.5, px: 0 }}>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                  {pageGroup.widgets.map(t => {
                                    const active = t.templateKey === selectedTemplateKey;
                                    const fuelLabel = getFuelTypeLabel(t.fuelType);
                                    return (
                                      <Tooltip key={t.templateKey} title={t.templateKey} placement="right">
                                        <Box
                                          onClick={() => handleSelectTemplate(t.templateKey)}
                                          sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'flex-start',
                                            gap: 1,
                                            width: '100%',
                                            px: 1.5,
                                            py: 1.5,
                                            ml: 1,
                                            cursor: 'pointer',
                                            borderRadius: 1,
                                            textAlign: 'left',
                                            border: active ? theme => `1px solid ${theme.palette.primary.main}` : '1px solid transparent',
                                            backgroundColor: active
                                              ? theme => theme.palette.mode === 'dark'
                                                ? alpha(theme.palette.primary.main, 0.16)
                                                : alpha(theme.palette.primary.main, 0.08)
                                              : 'transparent',
                                            '&:hover': {
                                              backgroundColor: theme => theme.palette.mode === 'dark'
                                                ? alpha(theme.palette.common.white, 0.08)
                                                : alpha(theme.palette.common.black, 0.04)
                                            },
                                            transition: 'all 0.2s ease-in-out'
                                          }}
                                        >
                                          <Chip
                                            size="small"
                                            variant="outlined"
                                            label={fuelLabel}
                                            sx={{
                                              fontSize: '0.75rem',
                                              height: 20,
                                              width: 70,
                                              px: 0.5,
                                              py: 0.5,
                                              borderColor: theme => alpha(theme.palette.primary.main, 0.5),
                                              color: 'primary.main',
                                              bgcolor: theme => alpha(theme.palette.primary.main, 0.1),
                                              flexShrink: 0,
                                              '& .MuiChip-label': { textAlign: 'center', width: '100%', px: 0 }
                                            }}
                                          />
                                          <Typography
                                            variant="body2"
                                            sx={{
                                              overflow: 'hidden',
                                              textOverflow: 'ellipsis',
                                              whiteSpace: 'nowrap',
                                              flex: 1,
                                              fontSize: '0.875rem',
                                              textAlign: 'left'
                                            }}
                                          >
                                            {t.name}
                                          </Typography>
                                        </Box>
                                      </Tooltip>
                                    );
                                  })}
                                </Box>
                              </AccordionDetails>
                            </Accordion>
                          );
                        })}
                      </Box>
                    ) : (
                    <>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      {channel.templates.map((template) => {
                        const isTemplateSelected = template.templateKey === selectedTemplateKey && !selectedSectionKey;
                        const isTemplateExpanded = expandedTemplates.has(template.templateKey);
                        const hasSections = template.sections.length > 0;

                        return (
                          <Box key={template.templateKey}>
                            {/* Template Row with Tooltip showing featureId */}
                            <Tooltip 
                              title={template.templateKey}
                              placement="right"
                              arrow
                              enterDelay={500}
                            >
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                  width: '100%',
                                  px: 1.5,
                                  py: 1,
                                  cursor: 'pointer',
                                  borderRadius: 1,
                                  border: isTemplateSelected
                                    ? theme => `1px solid ${theme.palette.primary.main}`
                                    : '1px solid transparent',
                                  backgroundColor: isTemplateSelected
                                    ? theme => theme.palette.mode === 'dark'
                                      ? alpha(theme.palette.primary.main, 0.16)
                                      : alpha(theme.palette.primary.main, 0.08)
                                    : 'transparent',
                                  '&:hover': {
                                    backgroundColor: theme => theme.palette.mode === 'dark'
                                      ? alpha(theme.palette.common.white, 0.08)
                                      : alpha(theme.palette.common.black, 0.04)
                                  },
                                  transition: 'all 0.2s ease-in-out'
                                }}
                                onClick={() => handleSelectTemplate(template.templateKey)}
                              >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
                                  {/* Use displayFuelLabel when available (e.g. 'DF' for USER_WELCOME), else derive from fuelType */}
                                  {(template.fuelType || template.displayFuelLabel) && (
                                    <Chip
                                      size="small"
                                      variant="outlined"
                                      label={template.displayFuelLabel || getFuelTypeLabel(template.fuelType)}
                                      sx={{
                                        fontSize: '0.75rem',
                                        height: 20,
                                        width: 70,
                                        px: 0.5,
                                        py: 0.5,
                                        borderColor: theme => alpha(theme.palette.primary.main, 0.5),
                                        color: 'primary.main',
                                        bgcolor: theme => alpha(theme.palette.primary.main, 0.1),
                                        flexShrink: 0,
                                        '& .MuiChip-label': {
                                          px: 0,
                                          textAlign: 'center',
                                          width: '100%'
                                        }
                                      }}
                                    />
                                  )}
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                      flex: 1,
                                      fontSize: '0.875rem',
                                      textAlign: 'left'
                                    }}
                                  >
                                    {template.name}
                                  </Typography>
                                </Box>
                                {hasSections && (
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleToggleTemplate(template.templateKey);
                                    }}
                                    sx={{ 
                                      p: 0.5,
                                      color: theme => theme.palette.mode === 'dark' ? '#fff' : theme.palette.text.secondary
                                    }}
                                  >
                                    {isTemplateExpanded ? (
                                      <ChevronUp size={16} />
                                    ) : (
                                      <ChevronDown size={16} />
                                    )}
                                  </IconButton>
                                )}
                              </Box>
                            </Tooltip>

                            {/* Sections List (nested under template) */}
                            {hasSections && isTemplateExpanded && (
                              <Box
                                sx={{
                                  ml: 2,
                                  mt: 0.5,
                                  borderLeft: theme => `1px solid ${theme.palette.divider}`,
                                  pl: 1
                                }}
                              >
                                {template.sections.map((section) => {
                                  const isSectionSelected =
                                    template.templateKey === selectedTemplateKey &&
                                    section.sectionKey === selectedSectionKey;

                                  return (
                                    <Tooltip
                                      key={section.sectionKey}
                                      title={section.sectionId}
                                      placement="right"
                                      arrow
                                      enterDelay={500}
                                    >
                                      <Box
                                        onClick={() => handleSelectSection(template.templateKey, section.sectionKey, section.sectionId, section.name)}
                                        sx={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: 1,
                                          px: 1.5,
                                          py: 0.75,
                                          cursor: 'pointer',
                                          borderRadius: 1,
                                          border: isSectionSelected
                                            ? theme => `1px solid ${theme.palette.primary.main}`
                                            : '1px solid transparent',
                                          backgroundColor: isSectionSelected
                                            ? theme => theme.palette.mode === 'dark'
                                              ? alpha(theme.palette.primary.main, 0.16)
                                              : alpha(theme.palette.primary.main, 0.08)
                                            : 'transparent',
                                          '&:hover': {
                                            backgroundColor: theme => theme.palette.mode === 'dark'
                                              ? alpha(theme.palette.common.white, 0.08)
                                              : alpha(theme.palette.common.black, 0.04)
                                          },
                                          transition: 'all 0.2s ease-in-out'
                                        }}
                                      >
                                        <LayoutGrid size={14} style={{ opacity: 0.6, flexShrink: 0 }} />
                                        <Typography
                                          variant="body2"
                                          sx={{
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                            fontSize: '0.8125rem',
                                            textAlign: 'left'
                                          }}
                                        >
                                          {section.name}
                                        </Typography>
                                      </Box>
                                    </Tooltip>
                                  );
                                })}
                              </Box>
                            )}
                          </Box>
                        );
                      })}
                    </Box>
                    </>
                    )}
                  </AccordionDetails>
                </Accordion>
              ))}
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

